import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import '../styles/inspire.css';
import '../styles/inspire-home.css';
import '../styles/service-page.css';

const TherapyForWomen = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const goToContact = () => {
    navigate('/', { state: { scrollTo: 'contact' } });
  };

  const helpItems = [
    "Burnout, emotional exhaustion, or feeling stretched too thin",
    "Identity shifts — losing yourself in motherhood, career, or caregiving",
    "Parenting stress, guilt, or the pressure to do it all",
    "Anxiety, overthinking, or constantly feeling on edge",
    "Life transitions — new baby, divorce, career change, empty nest",
    "Relationship struggles or feeling disconnected from your partner",
    "Grief, loss, or unresolved pain from the past",
    "The quiet feeling that something needs to change"
  ];

  const fitItems = [
    "You're carrying everyone else's emotions and losing yourself in the process",
    "You're experiencing burnout, overwhelm, or a life transition that feels unsteady",
    "You want more than surface-level advice — you want real, meaningful change",
    "You're craving a space that feels warm, grounded, and deeply human",
    "You know something needs to shift — you're just not sure where to start",
    "You're ready to reconnect with who you are beyond caregiving"
  ];

  return (
    <div className="therapy-site" data-testid="therapy-for-women-page">
      <Helmet>
        <title>Therapy for Women | Inspire Therapy Collective - Lake Nona, Orlando</title>
        <meta name="description" content="Therapy for women navigating burnout, identity shifts, parenting stress, and life transitions in Lake Nona, Orlando and throughout Florida and Virginia. A space to breathe, reset, and reconnect." />
      </Helmet>

      <Header />

      {/* Hero */}
      <section className="service-hero">
        <div 
          className="service-hero-overlay" 
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1638244398513-17b778d24efe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHx3b21hbiUyMHBlYWNlZnVsJTIwc2VsZiUyMGNhcmUlMjB3YXJtJTIwbGlnaHR8ZW58MHx8fHwxNzcyMDU3MDkyfDA&ixlib=rb-4.1.0&q=85')" }}
        />
        <div className="service-hero-gradient" />
        <div className="service-hero-content fade-in-up">
          <h1 className="gradient-text" data-testid="women-page-title">Therapy for Women</h1>
          <p className="service-tagline">
            Motherhood changes everything — including your relationship with yourself. This is a space to breathe, reset, and reconnect with who you are beyond caregiving.
          </p>
          <span className="service-location-tag">Lake Nona, Orlando FL &bull; Telehealth across Florida and Virginia</span>
        </div>
      </section>

      {/* Intro */}
      <section className="service-intro">
        <div className="container">
          <div className="service-intro-content fade-in-up">
            <p>
              Somewhere between managing the household, showing up for your kids, maintaining your career, and holding everything together — you lost track of yourself. Not all at once, but gradually. And now you're left with a quiet ache that something needs to change.
            </p>
            <p>
              I support women navigating burnout, identity shifts, parenting stress, emotional overwhelm, and the invisible weight of doing it all. This isn't just "How are you feeling?" therapy — it's "Who are you becoming?" work. I help women reconnect to their intuition, name what's been buried, and build a life that feels aligned.
            </p>
            <p>
              You deserve a space that's entirely yours — where you don't have to perform, manage, or hold it together. Where you can be honest about what's hard and start building something that feels like you again.
            </p>
          </div>
        </div>
      </section>

      {/* What I Help With */}
      <section className="service-help-section">
        <div className="container">
          <h2 className="heading-2 font-serif text-center fade-in-up gradient-text" data-testid="women-help-heading">What I Help With</h2>
          <div className="service-help-grid">
            {helpItems.map((item, index) => (
              <div key={index} className="service-help-item fade-in-up" style={{ animationDelay: `${index * 0.05}s` }}>
                <div className="service-help-icon">
                  <svg viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <linearGradient id={`helpIconWomen${index}`} x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#F9C8A8" />
                        <stop offset="50%" stopColor="#E6A9C0" />
                        <stop offset="100%" stopColor="#C4A8E2" />
                      </linearGradient>
                    </defs>
                    <circle cx="14" cy="14" r="10" stroke={`url(#helpIconWomen${index})`} strokeWidth="1.5" fill="none" opacity="0.3" />
                    <path d="M10 14L13 17L18 11" stroke={`url(#helpIconWomen${index})`} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <p>{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* My Approach */}
      <section className="service-approach">
        <div className="container">
          <h2 className="heading-2 font-serif text-center fade-in-up gradient-text">My Approach with Women</h2>
          <svg className="service-sun-divider fade-in" viewBox="0 0 80 60" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="sunDividerWomen" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#F9C8A8" />
                <stop offset="50%" stopColor="#E6A9C0" />
                <stop offset="100%" stopColor="#C4A8E2" />
              </linearGradient>
            </defs>
            <line x1="10" y1="45" x2="70" y2="45" stroke="url(#sunDividerWomen)" strokeWidth="1.5" opacity="0.35" />
            <path d="M 40 45 A 15 15 0 0 1 25 45 L 40 45 Z" fill="url(#sunDividerWomen)" opacity="0.75" />
          </svg>
          <div className="service-approach-content fade-in-up">
            <p>
              My work with women blends CBT, ACT, and Motivational Interviewing with mindfulness, somatic awareness, and narrative therapy. This isn't cookie-cutter therapy — it's deeply personalized to where you are right now and where you want to be.
            </p>
            <p>
              I help women reconnect to their intuition, name what's been buried, and build a life that feels aligned — not perfect, but honest. We'll work together to untangle the patterns that keep you stuck, set boundaries that actually hold, and create space for the version of yourself that's been waiting.
            </p>
            <p>
              I believe that when you support the mom, you support everyone around her. Healing doesn't happen in isolation — and neither does growth.
            </p>
          </div>
        </div>
      </section>

      {/* Good Fit */}
      <section className="service-fit-section">
        <div className="container">
          <h2 className="heading-2 font-serif text-center fade-in-up gradient-text">This Might Be a Good Fit If...</h2>
          <div className="service-fit-list">
            {fitItems.map((item, index) => (
              <div key={index} className="service-fit-item fade-in-up" style={{ animationDelay: `${index * 0.05}s` }}>
                <div className="fit-bullet" />
                <p>{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="service-cta">
        <div className="container">
          <div className="service-cta-content fade-in-up">
            <h2 className="heading-2 font-serif gradient-text">You Deserve This Space</h2>
            <p>
              You don't have to have it all figured out to start. You just have to show up. Let's talk about what you need and how I can support you.
            </p>
            <div className="hero-cta-group" style={{ justifyContent: 'center' }}>
              <button onClick={goToContact} className="btn btn-primary" data-testid="women-cta-primary">
                Book a Free 15-Min Consult
              </button>
              <button onClick={() => navigate('/', { state: { scrollTo: 'insurance' } })} className="btn btn-secondary-outline" data-testid="women-cta-secondary">
                Check Insurance & Book
              </button>
            </div>
            <div className="service-cta-details">
              <a href="tel:4072019895" className="cta-phone">(407) 201-9895</a>
              <span className="cta-location">Lake Nona, Orlando FL &bull; Telehealth across Florida and Virginia</span>
            </div>
            <button onClick={() => navigate('/', { state: { scrollTo: 'services' } })} className="explore-all-link" data-testid="explore-all-services-link">
              &larr; Explore All Services
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TherapyForWomen;
